import React, { useEffect, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { FormControl, FormHelperText, FormLabel, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

export type EntraGroup = { id: string; displayName: string };
type Option = EntraGroup & { people?: number; members?: string[] };

// "Ludvig Sellebråten, PostNord" reads as "Ludvig Sellebråten" in a list
// where everyone is from PostNord.
const shortName = (name: string) => name.replace(/, PostNord$/, '');

function whoIsIn(g: Option): string | undefined {
  if (g.people === undefined) return undefined;
  if (g.people === 0) return 'No people';
  const names = (g.members ?? []).map(shortName);
  const more = g.people - names.length;
  return [...names, ...(more > 0 ? [`+${more} more`] : [])].join(', ');
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Searches Entra security groups as you type through the backend's
// azure-ad-groups/search, so the list is the live directory, not a copy, and
// no Microsoft token reaches the browser. A pasted Object ID is always
// accepted, so the form still works when search is down.
export const EntraGroupPicker = ({
  onChange,
  formData,
  schema,
  uiSchema,
  rawErrors,
  required,
}: FieldExtensionComponentProps<EntraGroup[] | undefined>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string>();
  const value = formData ?? [];

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    if (GUID.test(q)) {
      setOptions([{ id: q.toLowerCase(), displayName: q.toLowerCase() }]);
      return undefined;
    }
    let live = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const base = await discoveryApi.getBaseUrl('azure-ad-groups');
        const res = await fetchApi.fetch(`${base}/search?q=${encodeURIComponent(q)}`);
        const body = await res.json();
        if (!res.ok) throw new Error(body.error ?? `Entra search failed (${res.status}).`);
        if (live) {
          setOptions(body.groups as Option[]);
          setProblem(undefined);
        }
      } catch (e) {
        if (live) {
          setOptions([]);
          setProblem(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (live) setLoading(false);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [input, discoveryApi, fetchApi]);

  const help = uiSchema?.['ui:help'] as string | undefined;
  return (
    <FormControl margin="normal" required={required} error={!!rawErrors?.length} fullWidth>
      <FormLabel>{schema.title ?? 'Entra groups'}</FormLabel>
      <Autocomplete
        multiple
        filterSelectedOptions
        options={options}
        value={value}
        loading={loading}
        filterOptions={o => o}
        getOptionLabel={g => g.displayName}
        getOptionSelected={(a, b) => a.id === b.id}
        renderOption={(g: Option) => (
          <span>
            <span>
              {g.displayName}
              {g.people !== undefined && (
                <span style={{ opacity: 0.7, marginLeft: 8 }}>
                  {g.people === 1 ? '1 person' : `${g.people} people`}
                </span>
              )}
              {g.displayName !== g.id && (
                <span style={{ opacity: 0.7, fontFamily: 'monospace', marginLeft: 8 }}>{g.id}</span>
              )}
            </span>
            {whoIsIn(g) && (
              <span style={{ display: 'block', opacity: 0.7, fontSize: '0.85em' }}>{whoIsIn(g)}</span>
            )}
          </span>
        )}
        noOptionsText={
          input.trim().length < 2 ? 'Type at least two letters of the group name' : 'No security group matches'
        }
        onInputChange={(_, v) => setInput(v)}
        onChange={(_, v) => onChange((v as Option[]).map(({ id, displayName }) => ({ id, displayName })))}
        renderInput={params => (
          <TextField
            {...params}
            inputProps={{ ...params.inputProps, 'aria-label': schema.title ?? 'Entra groups' }}
            placeholder="Search by group name, or paste an Object ID"
            required={required && value.length === 0}
          />
        )}
      />
      <FormHelperText error={!!problem}>{problem ?? help}</FormHelperText>
    </FormControl>
  );
};

export const EntraGroupPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'EntraGroupPicker',
    component: EntraGroupPicker,
    validation: (value: EntraGroup[] | undefined, validation) => {
      for (const g of value ?? []) {
        if (!GUID.test(g.id)) validation.addError(`${g.displayName} is not an Entra group Object ID`);
      }
    },
  }),
);
